<template>
  <div class="bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h3 class="text-[15px] font-semibold text-gray-900">Ads Settings</h3>
        <p class="text-sm text-gray-400 mt-0.5">Configure a slate and ad-tag URL for this channel</p>
      </div>
      <div class="flex items-center gap-3 shrink-0">
        <span v-if="statusBadge" :class="statusBadge.class">{{ statusBadge.label }}</span>
        <ToggleSwitch
          :key="toggleKey"
          :model-value="isEnabled"
          :disabled="toggleDisabled"
          @update:model-value="requestToggle"
        />
      </div>
    </div>

    <div class="p-5 space-y-4">
      <!-- Gating notice: this mirrors the create-endpoint's server-side
           preconditions so the user sees why the toggle is disabled without
           having to try it and read a toast. -->
      <div v-if="gateReason" class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
        <svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <p class="text-sm text-amber-700">{{ gateReason }}</p>
      </div>

      <!-- Slate picker -->
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1.5">Slate <span class="text-red-500">*</span></label>
        <div class="flex items-center gap-2">
          <select
            v-model="form.slateId"
            :disabled="fieldsDisabled"
            class="flex-1"
          >
            <option value="">Select a slate</option>
            <option v-for="slate in availableSlates" :key="slate._id" :value="slate._id">{{ slate.title }}</option>
          </select>
          <button
            type="button"
            @click="showSlatePreview = true"
            :disabled="!form.slateId"
            :class="[
              'shrink-0 h-[42px] w-[42px] rounded-lg border flex items-center justify-center transition-colors',
              form.slateId
                ? 'border-gray-200 bg-white text-gray-500 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]'
                : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
            ]"
            title="Preview slate"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Ad-tag URI -->
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1.5">Ads Tag URI <span class="text-red-500">*</span></label>
        <input
          v-model="form.ad_tag_uri"
          type="text"
          :disabled="fieldsDisabled"
          placeholder="https://..."
        />
        <p class="text-sm text-gray-400 mt-1.5">VAST or VMAP URL served to viewers by the ad server.</p>
      </div>
    </div>

    <!-- Confirm create / delete -->
    <ConfirmModal
      :show="showConfirm"
      :title="pendingAction === 'create' ? 'Enable Ads Settings' : 'Disable Ads Settings'"
      :message="pendingAction === 'create'
        ? 'Create the ads configuration for this channel? The channel must be streaming.'
        : 'Delete the ads configuration for this channel? The channel must be stopped first.'"
      :confirm-text="pendingAction === 'create' ? 'Enable' : 'Disable'"
      :danger="pendingAction === 'delete'"
      :loading="saving"
      @confirm="applyToggle"
      @cancel="cancelToggle"
    />

    <!-- Slate preview -->
    <MediaPlayerModal
      :show="showSlatePreview"
      title="Slate Preview"
      :formats="previewFormats"
      @close="showSlatePreview = false"
    />
  </div>
</template>

<script>
import ToggleSwitch from '../../../components/ToggleSwitch.vue'
import ConfirmModal from '../../../components/ConfirmModal.vue'
import MediaPlayerModal from '../../../components/MediaPlayerModal.vue'
import api from '../../../services/api.js'
import { notifyError, notifySuccess } from '../../../services/notifications.js'

export default {
  name: 'LiveAds',
  components: { ToggleSwitch, ConfirmModal, MediaPlayerModal },

  props: {
    livestreamId: { type: String, required: true },
    // Full livestream.ads object as it exists on the parent's model.
    // Shape: { status, ad_tag_uri, slate: { _id, url, gcp_id }, live_config_id }
    ads: { type: Object, default: () => ({}) },
    // Parent status flags: the create endpoint requires status !== 'published'
    // AND channel.status === 'streaming'; delete requires channel not streaming.
    liveStatus: { type: String, default: 'draft' },
    channelIsStreaming: { type: Boolean, default: false }
  },

  emits: ['updated'],

  data() {
    return {
      form: {
        slateId: '',
        ad_tag_uri: ''
      },
      availableSlates: [],
      showSlatePreview: false,
      showConfirm: false,
      pendingAction: null,
      saving: false,
      // Bumped whenever a toggle click is rejected (validation fail, user
      // cancels the confirm, save errors). Remounts ToggleSwitch so its
      // native <input> resyncs to `isEnabled` — otherwise the input keeps
      // the click-flipped state and the next click emits the opposite
      // value, making a create attempt look like a delete request.
      toggleKey: 0
    }
  },

  computed: {
    isEnabled() {
      return this.ads?.status === 'success' || this.ads?.status === 'provisioning'
    },
    fieldsDisabled() {
      // Fields lock once the config exists — the API rejects changes while
      // status is 'success', and 'provisioning' means a create is in flight.
      return this.isEnabled
    },
    statusBadge() {
      const s = this.ads?.status
      if (s === 'success')      return { label: 'Active',       class: 'badge-live' }
      if (s === 'provisioning') return { label: 'Provisioning', class: 'badge-warning' }
      if (s === 'failed')       return { label: 'Failed',       class: 'badge-error' }
      return null
    },
    gateReason() {
      if (this.isEnabled) return ''
      // if (this.liveStatus === 'published') return 'Live must be on Draft status to configure ads.'
      if (!this.channelIsStreaming) return 'Channel must be Streaming to configure ads.'
      return ''
    },
    toggleDisabled() {
      if (this.saving) return true
      // Enable path: needs draft live + streaming channel.
      if (!this.isEnabled) return !!this.gateReason
      // Disable path: server rejects delete while channel is streaming.
      return this.channelIsStreaming
    },
    selectedSlate() {
      return this.availableSlates.find(s => s._id === this.form.slateId) || null
    },
    previewFormats() {
      if (!this.form.slateId) return []
      return [{
        id: 'original',
        remoteUrl: this.$mediaUrl.slate(this.form.slateId),
        status: 'success'
      }]
    }
  },

  watch: {
    // Reseed the local form whenever the parent's ads object gets replaced
    // (e.g. after the create call comes back).
    ads: {
      immediate: true,
      deep: true,
      handler(v) {
        this.form.slateId = v?.slate?._id || ''
        this.form.ad_tag_uri = v?.ad_tag_uri || ''
      }
    }
  },

  async mounted() {
    await this.loadSlates()
  },

  methods: {
    async loadSlates() {
      try {
        const res = await api.get('/slates', { params: { status: 'success', limit: 200 }, silent: true })
        this.availableSlates = res.data?.items || []
      } catch (err) {
        notifyError(err?.message || 'Failed to load slates')
      }
    },

    validate() {
      if (!this.form.slateId)         { notifyError('Slate is required');       return false }
      if (!this.form.ad_tag_uri?.trim()) { notifyError('Ads Tag URI is required'); return false }
      return true
    },

    requestToggle(nextValue) {
      if (this.saving) return
      const action = nextValue ? 'create' : 'delete'
      if (action === 'create' && !this.validate()) {
        this.toggleKey++
        return
      }
      this.pendingAction = action
      this.showConfirm = true
    },

    cancelToggle() {
      this.showConfirm = false
      this.pendingAction = null
      this.toggleKey++
    },

    async applyToggle() {
      if (!this.pendingAction) return
      this.saving = true
      try {
        const payload = this.pendingAction === 'create'
          ? {
              ad_tag_uri: this.form.ad_tag_uri,
              slate: this.selectedSlate ? {
                _id: this.selectedSlate._id,
                url: this.selectedSlate.url?.original || null,
                gcp_id: this.selectedSlate.gcp_id || null
              } : { _id: this.form.slateId }
            }
          : {}
        const { data } = await api.put(`/livestreams/${this.livestreamId}/ads/${this.pendingAction}`, payload)
        this.$emit('updated', data?.ads || {})
        notifySuccess(this.pendingAction === 'create'
          ? 'Ads settings created'
          : 'Ads settings deleted'
        )
        this.showConfirm = false
        this.pendingAction = null
      } catch (err) {
        notifyError(err?.message || 'Failed to update ads settings')
        this.toggleKey++
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
